import { IonButton, IonCard, IonIcon, IonItem, IonLabel, IonToast } from "@ionic/react";
import { downloadOutline, logoWhatsapp } from "ionicons/icons";
import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

interface Props {
  whatsappNo: string;
  businessName?: string;
}

// Mirrors fyllo-dashboard's "Retailer Onboarding QR" (InventoryLocation.tsx)
// -- the QR encodes the raw WhatsApp number itself (not a wa.me link), since
// it's meant to be scanned by the Fyllo app's own onboarding flow, not a
// phone camera. Kept in lockstep with the dashboard's encoding so a QR
// printed from either app onboards the same way.
const RetailerOnboardingQr: React.FC<Props> = ({ whatsappNo, businessName }) => {
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const [sharing, setSharing] = useState(false);
  const [toast, setToast] = useState("");

  function downloadQr() {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `fyllo-retailer-qr-${whatsappNo}.png`;
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function shareQr() {
    const canvas = qrCanvasRef.current;
    if (!canvas || sharing) return;
    setSharing(true);
    try {
      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const shareText = `Fyllo Retailer QR for ${businessName || whatsappNo}`;
      const file = blob ? new File([blob], `fyllo-retailer-qr-${whatsappNo}.png`, { type: "image/png" }) : null;

      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Fyllo Retailer QR", text: shareText });
        return;
      }

      // No native file-share support (e.g. desktop browser) -- same
      // workaround the dashboard uses: download the QR image, then open
      // WhatsApp with a prompt to attach it, since wa.me can't pre-attach
      // media.
      downloadQr();
      window.open(
        `https://wa.me/?text=${encodeURIComponent(`${shareText}. Please attach the QR image that was just downloaded and send it here.`)}`,
        "_blank"
      );
    } catch (err: any) {
      if (err?.name !== "AbortError") setToast("Failed to share QR code.");
    } finally {
      setSharing(false);
    }
  }

  return (
    <IonCard>
      <IonItem lines="none">
        <IonLabel>Retailer Onboarding QR</IonLabel>
      </IonItem>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "0 16px 16px" }}>
        <QRCodeCanvas ref={qrCanvasRef} value={whatsappNo} size={160} includeMargin />
        <span className="secondary-text">{whatsappNo}</span>
        <div style={{ display: "flex", gap: 12 }}>
          <IonButton size="small" fill="outline" onClick={downloadQr}>
            <IonIcon icon={downloadOutline} slot="start" />
            Download
          </IonButton>
          <IonButton size="small" color="success" disabled={sharing} onClick={shareQr}>
            <IonIcon icon={logoWhatsapp} slot="start" />
            Share
          </IonButton>
        </div>
      </div>

      <IonToast isOpen={!!toast} onDidDismiss={() => setToast("")} message={toast} duration={2500} color="danger" />
    </IonCard>
  );
};

export default RetailerOnboardingQr;
